import { useLocation } from "wouter"
import { Card } from "@/components/ui/card"
import { Image as ImageIcon } from "lucide-react"
import type { CockpitWirkung, WirkungKpi } from "@shared/dashboard"
import { formatDelta, formatNumber } from "@/lib/cockpitFormat"

// "Wirkung" — impact before activity. Reach is attributed by publish week (the Late
// metrics are cumulative per post, there is no time series), deltas compare with the
// four weeks before. Renders an honest empty state until the ingestion has written data.

function Kpi({ label, kpi, testId }: { label: string; kpi: WirkungKpi; testId: string }) {
  const delta = formatDelta(kpi.deltaPct)
  const negative = (kpi.deltaPct ?? 0) < 0
  return (
    <div className="rounded-lg bg-muted/50 px-3 py-2.5" data-testid={testId}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-[22px] font-bold leading-tight tabular-nums">{formatNumber(kpi.value)}</p>
      <p className={`text-[11.5px] font-semibold tabular-nums ${delta ? (negative ? "text-red-600 dark:text-red-400" : "text-primary") : "text-muted-foreground"}`}>
        {delta ? `${delta} zur Vorperiode` : "keine Vorperiode"}
      </p>
    </div>
  )
}

function ReachChart({ series }: { series: CockpitWirkung["series"] }) {
  if (series.length === 0) return null
  const W = 520
  const H = 150
  const left = 40
  const right = 500
  const top = 20
  const bottom = 130
  const max = Math.max(1, ...series.map((s) => s.reach))
  const step = series.length > 1 ? (right - left - 40) / (series.length - 1) : 0
  const pts = series.map((s, i) => ({
    x: left + 20 + i * step,
    y: bottom - (s.reach / max) * (bottom - top),
    label: s.label,
    reach: s.reach,
  }))
  const line = pts.map((p) => `${p.x} ${p.y}`).join(" L ")
  const area = `M ${line} L ${pts[pts.length - 1].x} ${bottom} L ${pts[0].x} ${bottom} Z`
  const last = pts[pts.length - 1]
  const gridYs = [top, top + (bottom - top) / 3, top + (2 * (bottom - top)) / 3, bottom]

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="block h-auto w-full" role="img" aria-label="Reichweite pro Woche, vier Wochen">
      <defs>
        <linearGradient id="wirkung-fill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="hsl(var(--primary))" stopOpacity="0.35" />
          <stop offset="1" stopColor="hsl(var(--primary))" stopOpacity="0" />
        </linearGradient>
      </defs>
      <g stroke="hsl(var(--border))" strokeWidth="1">
        {gridYs.map((y) => (
          <line key={y} x1={left} x2={right} y1={y} y2={y} />
        ))}
      </g>
      <path d={area} fill="url(#wirkung-fill)" />
      <path d={`M ${line}`} fill="none" stroke="hsl(var(--primary))" strokeWidth="2.2" strokeLinejoin="round" />
      {pts.slice(0, -1).map((p) => (
        <circle key={p.label} cx={p.x} cy={p.y} r="2.5" fill="hsl(var(--primary))" />
      ))}
      <circle cx={last.x} cy={last.y} r="5" fill="hsl(var(--primary))" stroke="hsl(var(--card))" strokeWidth="2" />
      <text x={last.x} y={Math.max(12, last.y - 12)} textAnchor="middle" className="fill-foreground text-[10px] font-bold">
        {formatNumber(last.reach)}
      </text>
      {pts.map((p) => (
        <text key={`l-${p.label}`} x={p.x} y={H - 4} textAnchor="middle" className="fill-muted-foreground text-[10px]">
          {p.label}
        </text>
      ))}
    </svg>
  )
}

export function WirkungPanel({ wirkung }: { wirkung: CockpitWirkung | null }) {
  const [, setLocation] = useLocation()

  return (
    <Card className="p-4 sm:p-5" data-testid="cockpit-wirkung">
      <div className="mb-3.5 flex items-baseline gap-3">
        <h2 className="font-playfair text-[19px] font-bold">Wirkung</h2>
        <span className="ml-auto text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Letzte 4 Wochen</span>
      </div>

      {!wirkung ? (
        <p className="rounded-lg bg-muted/50 px-4 py-6 text-center text-sm text-muted-foreground" data-testid="wirkung-empty">
          Sobald Ihre ersten Beiträge Reichweite sammeln, sehen Sie hier, was wirkt.
        </p>
      ) : (
        <>
          <div className="mb-3.5 grid grid-cols-3 gap-2.5">
            <Kpi label="Reichweite" kpi={wirkung.reach} testId="kpi-reach" />
            <Kpi label="Interaktionen" kpi={wirkung.engagement} testId="kpi-engagement" />
            <Kpi label="Beiträge" kpi={wirkung.posts} testId="kpi-posts" />
          </div>
          <ReachChart series={wirkung.series} />
          {wirkung.topPost && (
            <button
              type="button"
              onClick={() => wirkung.topPost?.href && setLocation(wirkung.topPost.href)}
              disabled={!wirkung.topPost.href}
              className="mt-3.5 grid w-full grid-cols-[88px_1fr] gap-3.5 border-t border-border/60 pt-3.5 text-left disabled:cursor-default"
              data-testid="wirkung-top-post"
            >
              <span className="relative block aspect-square overflow-hidden rounded-lg bg-muted">
                {wirkung.topPost.imageUrl ? (
                  <img src={wirkung.topPost.imageUrl} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
                ) : (
                  <span className="absolute inset-0 grid place-items-center bg-gradient-to-br from-emerald-100 to-emerald-200 dark:from-emerald-950 dark:to-emerald-900">
                    <ImageIcon className="h-5 w-5 text-foreground/30" aria-hidden="true" />
                  </span>
                )}
              </span>
              <span className="min-w-0 text-[12.5px]">
                <span className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Top-Beitrag</span>
                <span className="mt-0.5 block font-semibold leading-snug text-foreground">„{wirkung.topPost.title}“</span>
                <span className="mt-1.5 block text-[11.5px] tabular-nums text-muted-foreground">
                  <span className="mr-2.5">{wirkung.topPost.platform}</span>
                  <span className="mr-2.5">{formatNumber(wirkung.topPost.reach)} Reichweite</span>
                  <span className="mr-2.5">{formatNumber(wirkung.topPost.reactions)} Reaktionen</span>
                  <span>{formatNumber(wirkung.topPost.comments)} Kommentare</span>
                </span>
              </span>
            </button>
          )}
        </>
      )}
    </Card>
  )
}
